<script setup lang="ts">
import Accordion from "@/components/Accordion.vue"
import AccordionItem from "@/components/AccordionItem.vue"
import type { ChampionshipCategory } from "@/model/championship/championship-category"
import type { ChampionshipEventDay } from "@/model/championship/championship-event-day"
import { useHttpGet } from "@/vf"
import _ from "lodash"
import { computed } from "vue"

const props = defineProps<{
    element: any
}>()

const { data } = await useHttpGet<ChampionshipEventDay[]>(
    "/championship/event-day/" + props.element.config.selectedChampionship + "/schedule-categories",
)

// Groups categories by CategoryGroup, filters out categories without registrations and empty groups
const eventDays = computed(() =>
    (data.value ?? []).map(eventDay => {
        const categories = (eventDay as ChampionshipEventDay & { categories?: ChampionshipCategory[] }).categories ?? []
        const categoryGroups = _(categories)
            .filter(cc => !cc.hasNoRegistrations)
            .groupBy(cc => cc.category?.group?.id)
            .map(cats => ({
                ...cats[0].category!.group!,
                categories: _.orderBy(cats, cc => cc.category?.sortOrder),
            }))
            .filter(g => !!g.id && g.categories.length > 0)
            .orderBy("sortOrder")
            .value()
        return { ...eventDay, categoryGroups }
    }),
)
</script>

<template>
    <div class="schedule-category-overview">
        <div class="row">
            <div v-for="eventDay in eventDays" :key="eventDay.id" class="col-md">
                <h4 class="mb-3">{{ eventDay.name }}</h4>
                <Accordion layout="list">
                    <AccordionItem
                        v-for="(group, index) in eventDay.categoryGroups"
                        :key="group.id"
                        :id="`schedule-cat-${eventDay.id}-${index}`"
                        :title="group.name ?? ''"
                    >
                        <ul>
                            <li v-for="category in group.categories" :key="category.id" class="py-1">
                                {{ category.name }}
                            </li>
                        </ul>
                    </AccordionItem>
                </Accordion>
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
:deep(.accordion-list) {
    > li {
        list-style-type: disclosure-closed;
        cursor: pointer;

        h2 {
            display: inline;
            font-size: 1rem;
            margin: 0;
            padding: 0.4rem 0;
            cursor: pointer;
        }

        &.active {
            list-style-type: disclosure-open;
        }

        ul {
            list-style-type: disc;
        }
    }
}
</style>
